import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Barcode, BookMarked, ExternalLink, Library, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumberWithCommas } from "@/lib/formatters";

export interface CatalogMatchBiblioSummary {
  title: string;
  authors: string[];
  isbn?: string | null;
  issn?: string | null;
  callNumber?: string | null;
  lcClassification?: string | null;
  coverUrl?: string | null;
}

interface CatalogExistingBiblioSummaryProps {
  biblio: CatalogMatchBiblioSummary;
  existingCopyCount: number;
  biblioId: string;
  matchLabel?: "ISBN" | "ISSN";
}

export function CatalogExistingBiblioSummary({
  biblio,
  existingCopyCount,
  biblioId,
  matchLabel,
}: CatalogExistingBiblioSummaryProps) {
  const authors = biblio.authors?.filter(Boolean).join(", ");
  const callNumber = biblio.callNumber || biblio.lcClassification;
  const identifier = matchLabel === "ISSN" ? biblio.issn : (biblio.isbn ?? biblio.issn);
  const identifierLabel = matchLabel ?? (biblio.isbn ? "ISBN" : biblio.issn ? "ISSN" : "ID");

  return (
    <Card className="overflow-hidden border-primary/20 bg-primary/5 shadow-sm">
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start">
        {biblio.coverUrl ? (
          <img
            src={biblio.coverUrl}
            alt=""
            className="h-24 w-16 shrink-0 rounded object-cover shadow-sm"
          />
        ) : null}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start gap-2">
            <Library className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <h2 className="text-lg font-semibold leading-tight text-gray-900">{biblio.title}</h2>
          </div>
          {authors ? (
            <p className="flex items-center gap-1.5 text-sm text-gray-600">
              <User className="h-4 w-4 shrink-0 text-gray-400" />
              {authors}
            </p>
          ) : null}
          {callNumber ? (
            <p className="flex items-center gap-1.5 text-sm text-gray-600">
              <BookMarked className="h-4 w-4 shrink-0 text-gray-400" />
              {callNumber}
            </p>
          ) : null}
          {identifier ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded px-2.5 py-1",
                "bg-white font-mono text-sm text-gray-700 ring-1 ring-gray-200"
              )}
            >
              <Barcode className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-sans text-gray-500">{identifierLabel}</span>
              {identifier}
            </span>
          ) : null}
          <p className="text-sm text-gray-700">
            Already in your catalog (<strong>{formatNumberWithCommas(existingCopyCount)}</strong>{" "}
            {existingCopyCount === 1 ? "copy" : "copies"})
          </p>
          <Button variant="link" className="h-auto p-0 text-primary" asChild>
            <Link to={`/catalog/physical/${biblioId}`}>
              View full record
              <ExternalLink className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
